import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import {
  COLORS_TO_BE_REVIEWED,
  FIGMA_COLORS,
} from "components-redesign/styles/colors";
import { Tooltip } from "components-redesign/Tooltip/Tooltip";
import {
  TypographyLabelLarge,
  TypographyLabelSmall,
} from "components-redesign/Typography/Typography";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface SegmentedControlOption {
  icon?: React.ReactNode;
  label: string;
  value: string;
  disabled?: boolean;
  tooltip?: string;
}

interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value?: string;
  onChange?: (value: string) => void;
  id?: string;
  block?: boolean;
  align?: "left" | "right" | "center";
  label?: string;
}

const ControlContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
}));

const SegmentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "block",
})<{ block?: boolean }>(({ block, theme }) => ({
  position: "relative",
  display: "flex",
  borderRadius: 28,
  background: COLORS_TO_BE_REVIEWED.lightGrayBackground,
  padding: 4,
  width: block ? "100%" : "fit-content",
}));

const SegmentButton = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isSelected" &&
    prop !== "isDisabled" &&
    prop !== "block" &&
    prop !== "textAlign",
})<{
  isSelected?: boolean;
  isDisabled?: boolean;
  block?: boolean;
  textAlign: "left" | "right" | "center";
}>(({ isSelected, isDisabled, block, textAlign, theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent:
    textAlign === "left"
      ? "flex-start"
      : textAlign === "right"
        ? "flex-end"
        : "center",
  gap: 8,
  padding: "8px 16px",
  borderRadius: 24,
  cursor: isDisabled ? "not-allowed" : "pointer",
  color: isDisabled ? "#AAA" : "#4A4459",
  zIndex: 1,
  transition: "color 0.3s ease",
  opacity: isDisabled ? 0.5 : 1,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  whiteSpace: "nowrap",
  userSelect: "none",
  flex: block ? 1 : "initial",
}));

const Selection = styled(Box)<{ width: number; left: number }>(
  ({ width, left, theme }) => ({
    position: "absolute",
    top: 4,
    left,
    width,
    height: "calc(100% - 8px)",
    borderRadius: 24,
    backgroundColor: FIGMA_COLORS.secondaryContainerLight,
    transition: "all 0.3s ease",
    zIndex: 0,
  })
);

export const SegmentedControl = ({
  options,
  value,
  onChange,
  id,
  block,
  align = "center",
  label,
}: SegmentedControlProps) => {
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    value
  );
  const [selectionStyle, setSelectionStyle] = useState({ width: 0, left: 0 });
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const currentStateRef = useRef({ selectedOption, options });
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    currentStateRef.current = { selectedOption, options };
  }, [selectedOption, options]);

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  const calculateSelectionStyle = useCallback(
    (stateSource: {
      selectedOption?: string;
      options: SegmentedControlOption[];
    }) => {
      const { selectedOption: currentSelectedOption, options: currentOptions } =
        stateSource;

      if (currentSelectedOption === undefined) return;

      const index = currentOptions.findIndex(
        (option) => option.value === currentSelectedOption
      );
      if (index === -1 || !optionRefs.current[index]) return;

      const element = optionRefs.current[index];
      if (element) {
        setSelectionStyle({
          width: element.offsetWidth,
          left: element.offsetLeft,
        });
      }
    },
    []
  );

  useEffect(() => {
    calculateSelectionStyle({ selectedOption, options });
  }, [selectedOption, options, block, calculateSelectionStyle]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      calculateSelectionStyle(currentStateRef.current);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleOptionClick = (option: SegmentedControlOption) => {
    if (option.disabled) return;
    setSelectedOption(option.value);
    onChange?.(option.value);
  };

  return (
    <ControlContainer
      id={id}
      ref={containerRef}>
      {label && (
        <TypographyLabelSmall style={{ color: "#AAA" }}>
          {label}
        </TypographyLabelSmall>
      )}
      <SegmentWrapper block={block}>
        {options.length > 0 && selectedOption !== undefined && (
          <Selection
            width={selectionStyle.width}
            left={selectionStyle.left}
          />
        )}
        {options.map((option, index) => {
          const buttonContent = (
            <SegmentButton
              key={option.value}
              ref={(el) => {
                optionRefs.current[index] = el as HTMLDivElement;
              }}
              isSelected={selectedOption === option.value}
              isDisabled={option.disabled}
              block={block}
              textAlign={align}
              onClick={() => handleOptionClick(option)}
              data-qa={id ? `${id}-option-${option.value}` : undefined}
              role="button"
              aria-pressed={selectedOption === option.value}
              aria-disabled={option.disabled}>
              {option.icon && (
                <Box
                  component="span"
                  sx={{ display: "flex", alignItems: "center" }}>
                  {option.icon}
                </Box>
              )}
              <TypographyLabelLarge>{option.label}</TypographyLabelLarge>
            </SegmentButton>
          );

          if (option.tooltip) {
            return (
              <Tooltip
                key={option.value}
                title={option.tooltip}>
                {buttonContent}
              </Tooltip>
            );
          }

          return buttonContent;
        })}
      </SegmentWrapper>
    </ControlContainer>
  );
};

export default SegmentedControl;
